import { useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layouts/DashboardLayout';
import Toast from '../components/ui/Toast';
import Modal from '../components/ui/Modal';
import ResumePreviewModal, { EyeIcon } from '../components/ui/ResumePreviewModal';
import DeliveryModeConfirmModal from '../components/ui/DeliveryModeConfirmModal';
import { useAuth } from '../hooks/useAuth';
import apiClient from '../clients/apiClient';

const PLAN_LABELS: Record<string, string> = { free: 'Free', starter: 'Starter', growth: 'Growth', pro: 'Pro' };
const PLAN_COLORS: Record<string, string> = {
  free: 'bg-bg text-hint border border-border',
  starter: 'bg-blue-50 text-blue-600 border border-blue-200',
  growth: 'bg-g-light text-g-text border border-g-border',
  pro: 'bg-amber-50 text-amber-700 border border-amber-200',
};

export default function Settings() {
  const { user, logout, refreshUser } = useAuth();

  const [showDelete, setShowDelete] = useState(false);
  const [accountDeleting, setAccountDeleting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showResumePreview, setShowResumePreview] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [resumePdfUploading, setResumePdfUploading] = useState(false);
  const [resumePdfDeleting, setResumePdfDeleting] = useState(false);
  const [gmailDisconnecting, setGmailDisconnecting] = useState(false);
  const [autoSendUpdating, setAutoSendUpdating] = useState(false);
  const [confirmDeliveryMode, setConfirmDeliveryMode] = useState<boolean | null>(null);

  const handleUpdateFollowUpAutoSend = async (autoSend: boolean) => {
    setAutoSendUpdating(true);
    try {
      await apiClient.patch('/api/user/profile', { follow_up_auto_send: autoSend });
      await refreshUser();
      setToast({
        message: autoSend
          ? 'Auto-send enabled: follow-ups will send after 24h if unreviewed'
          : 'Follow-ups set to "Always ask me first" (safe confirmation mode)',
        type: 'success',
      });
    } catch {
      setToast({ message: 'Failed to update follow-up preference', type: 'error' });
    } finally {
      setAutoSendUpdating(false);
    }
  };

  const handleConfirmDeliveryMode = async () => {
    if (confirmDeliveryMode === null) return;
    const target = confirmDeliveryMode;
    await handleUpdateFollowUpAutoSend(target);
    setConfirmDeliveryMode(null);
  };

  const creditsUsedPct = user
    ? Math.round((user.credits_remaining / (user.credits_remaining + user.credits_used)) * 100)
    : 0;

  const handleResumePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResumePdfUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await apiClient.post('/api/user/resume-pdf', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshUser();
      setToast({ message: 'Resume uploaded ✓', type: 'success' });
    } catch {
      setToast({ message: 'Failed to upload resume. Please try again.', type: 'error' });
    } finally {
      setResumePdfUploading(false);
      e.target.value = '';
    }
  };

  const handleAccountDelete = async () => {
    setAccountDeleting(true);
    try {
      await apiClient.delete('/api/auth/account');
    } catch {
      setAccountDeleting(false);
      setShowDelete(false);
      setToast({ message: 'Failed to delete account. Please try again.', type: 'error' });
      return;
    }
    // Account is gone server-side; logout clears local auth state and redirects home.
    await logout();
  };

  const handleResumePdfDelete = async () => {
    setResumePdfDeleting(true);
    try {
      await apiClient.delete('/api/user/resume-pdf');
      await refreshUser();
      setToast({ message: 'Resume removed', type: 'info' });
    } catch {
      setToast({ message: 'Failed to remove resume', type: 'error' });
    } finally {
      setResumePdfDeleting(false);
    }
  };

  const handleGmailDisconnect = async () => {
    setGmailDisconnecting(true);
    try {
      await apiClient.post('/api/user/gmail/disconnect');
      await refreshUser();
      setToast({ message: 'Gmail disconnected', type: 'info' });
    } catch {
      setToast({ message: 'Failed to disconnect Gmail', type: 'error' });
    } finally {
      setGmailDisconnecting(false);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const canFollowUp = user?.plan === 'growth' || user?.plan === 'pro';

  return (
    <DashboardLayout showBack backLabel="Back to Dashboard" backTo="/dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-[28px] sm:text-[34px] font-semibold text-ink leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
            Settings
          </h1>
          <p className="text-sm text-muted mt-1">
            Manage your account, plan features, credits, and outreach assets.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* ── Left Column: Profile & Your Plan ── */}
          <div className="space-y-6">
            {/* PROFILE */}
            <div className="bg-card border border-border rounded-card p-5 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>Profile</h2>
                {/* Mobile-only header Logout pill */}
                <button
                  type="button"
                  onClick={() => setShowLogoutModal(true)}
                  className="sm:hidden px-2.5 py-1.5 rounded-pill text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-colors active:scale-95 shadow-sm"
                >
                  Log out
                </button>
              </div>

              <div className="flex items-center gap-4 mb-5">
                <div className="h-14 w-14 rounded-full bg-g-light border border-g-border flex items-center justify-center text-g font-bold text-lg flex-shrink-0">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-base sm:text-lg text-ink truncate">{user?.name ?? '—'}</p>
                  <p className="text-sm text-muted truncate">{user?.email ?? '—'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-2.5">
                {user?.gmail_connected && (
                  <>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-pill bg-g-light text-g-text border border-g-border text-xs font-semibold">
                      <svg className="h-3.5 w-3.5 text-g flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Gmail Connected
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowDisconnectModal(true)}
                      disabled={gmailDisconnecting}
                      className="px-2.5 py-1.5 rounded-pill text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50 active:scale-95 shadow-sm"
                    >
                      {gmailDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                  </>
                )}
                {/* Desktop: all 3 buttons sit side-by-side on the same line */}
                <button
                  type="button"
                  onClick={() => setShowLogoutModal(true)}
                  className="hidden sm:inline-flex px-2.5 py-1.5 rounded-pill text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition-colors active:scale-95 shadow-sm"
                >
                  Log out
                </button>
              </div>
            </div>

            {/* PLAN */}
            <div className="bg-card border border-border rounded-card p-5 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>Your Plan</h2>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${PLAN_COLORS[user?.plan ?? 'free']}`}>
                  {PLAN_LABELS[user?.plan ?? 'free'] ?? 'Free'}
                </span>
              </div>
              <div className="bg-bg rounded-card border border-border p-4 space-y-2 text-sm mb-4">
                {[
                  { label: 'Auto follow-ups', ok: canFollowUp },
                  { label: 'Direct send', ok: canFollowUp },
                  { label: 'Resume attachment', ok: canFollowUp },
                  { label: 'Better email quality', ok: canFollowUp },
                  { label: 'Best email quality (Opus)', ok: user?.plan === 'pro' },
                  { label: 'Highest email verification', ok: user?.plan === 'pro' },
                ].map(({ label, ok }) => (
                  <div key={label} className="flex items-center gap-2">
                    {ok ? (
                      <svg className="h-4 w-4 text-g flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-hint flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={ok ? 'text-ink font-medium' : 'text-hint'}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* FOLLOW-UP DELIVERY MODE */}
            <div className="bg-card border border-border rounded-card p-5 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>
                  Follow-up Delivery Mode
                </h2>
                {!canFollowUp && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    Growth / Pro
                  </span>
                )}
              </div>
              <p className="text-xs text-muted mb-4">
                Choose how automated follow-ups are confirmed and sent.
              </p>

              <div className="space-y-3">
                {/* Option 1: Always ask me first (Default) */}
                <label
                  onClick={() => {
                    if (canFollowUp && !autoSendUpdating && user?.follow_up_auto_send) {
                      setConfirmDeliveryMode(false);
                    }
                  }}
                  className={`relative flex items-start gap-3 p-3.5 rounded-2xl border transition-all cursor-pointer ${
                    !user?.follow_up_auto_send
                      ? 'bg-emerald-50/60 border-emerald-500/60 shadow-sm'
                      : 'bg-bg border-border hover:border-gray-300'
                  } ${!canFollowUp || autoSendUpdating ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  <input
                    type="radio"
                    name="follow_up_delivery_mode"
                    checked={!user?.follow_up_auto_send}
                    onChange={() => {
                      if (canFollowUp && !autoSendUpdating && user?.follow_up_auto_send) {
                        setConfirmDeliveryMode(false);
                      }
                    }}
                    disabled={!canFollowUp || autoSendUpdating}
                    className="mt-0.5 text-g focus:ring-g h-4 w-4 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-ink">Always ask me first</span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                        Default
                      </span>
                    </div>
                    <p className="text-xs text-muted mt-1 leading-relaxed">
                      You'll receive an email with a direct preview link on Day 3. The email is never sent without your explicit review and confirmation.
                    </p>
                  </div>
                </label>

                {/* Option 2: Auto-send if unreviewed after 24h */}
                <label
                  onClick={() => {
                    if (canFollowUp && !autoSendUpdating && !user?.follow_up_auto_send) {
                      setConfirmDeliveryMode(true);
                    }
                  }}
                  className={`relative flex items-start gap-3 p-3.5 rounded-2xl border transition-all cursor-pointer ${
                    user?.follow_up_auto_send
                      ? 'bg-amber-50/60 border-amber-500/60 shadow-sm'
                      : 'bg-bg border-border hover:border-gray-300'
                  } ${!canFollowUp || autoSendUpdating ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  <input
                    type="radio"
                    name="follow_up_delivery_mode"
                    checked={Boolean(user?.follow_up_auto_send)}
                    onChange={() => {
                      if (canFollowUp && !autoSendUpdating && !user?.follow_up_auto_send) {
                        setConfirmDeliveryMode(true);
                      }
                    }}
                    disabled={!canFollowUp || autoSendUpdating}
                    className="mt-0.5 text-amber-600 focus:ring-amber-500 h-4 w-4 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-ink">Auto-send if I don't respond within 24h</span>
                    </div>
                    <p className="text-xs text-muted mt-1 leading-relaxed">
                      You'll receive an email notice to review. If you don't approve or skip within 24 hours, the follow-up will send automatically.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* ── Right Column: Credits & Resume ── */}
          <div className="space-y-6">
            {/* CREDITS */}
            <div className="bg-card border border-border rounded-card p-5 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>Credits & Balance</h2>
                <Link to="/pricing" className="inline-flex items-center gap-1.5 text-xs font-semibold text-g hover:underline">
                  Buy credits →
                </Link>
              </div>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-5xl font-bold text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>
                  {user?.credits_remaining ?? '—'}
                </div>
                <div>
                  <p className="font-semibold text-ink text-sm sm:text-base">credits remaining</p>
                  <p className="text-xs sm:text-sm text-muted">{user?.credits_used ?? 0} used this month</p>
                </div>
              </div>
              <div className="h-2 rounded-full bg-border mb-1.5 overflow-hidden">
                <div className="h-full bg-g rounded-full transition-all" style={{ width: `${creditsUsedPct}%` }} />
              </div>
              <div className="flex justify-between text-xs text-hint mb-4">
                <span>{user?.credits_remaining ?? 0} available</span>
                <span>{user?.credits_used ?? 0} sent</span>
              </div>
              <div className="border-t border-border pt-4">
                <Link to="/pricing" className="inline-flex items-center gap-1.5 text-sm font-semibold text-g hover:underline">
                  View plans &amp; pricing →
                </Link>
              </div>
            </div>

            {/* RESUME ATTACHMENT (Growth/Pro only) */}
            {canFollowUp && (
              <div className="bg-card border border-border rounded-card p-5 sm:p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-ink mb-1" style={{ fontFamily: 'Instrument Serif, serif' }}>Resume Attachment</h2>
                <p className="text-sm text-muted mb-4">Attach your resume PDF to outreach emails automatically.</p>
                {user?.resume_pdf_path ? (
                  <div className="flex items-center justify-between gap-3 bg-g-light border border-g-border rounded-card px-4 py-3 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <svg className="h-4 w-4 text-g flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium text-g-text truncate">Resume.pdf</span>
                      <span className="text-xs text-g font-semibold">uploaded</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        id="resume-preview-btn"
                        onClick={() => setShowResumePreview(true)}
                        title="Preview resume"
                        className="flex items-center gap-1.5 text-xs font-semibold text-muted hover:text-ink border border-border rounded-input px-2.5 py-1.5 hover:bg-bg transition-colors"
                      >
                        <EyeIcon className="h-3.5 w-3.5" />
                        Preview
                      </button>
                      <button
                        onClick={handleResumePdfDelete}
                        disabled={resumePdfDeleting}
                        className="text-xs text-red-500 hover:text-red-700 font-semibold px-2 py-1.5 transition-colors disabled:opacity-50"
                      >
                        {resumePdfDeleting ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-hint mb-3">No resume uploaded yet.</p>
                )}
                <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-input text-sm font-semibold border border-border hover:border-g text-muted hover:text-g transition-colors cursor-pointer ${resumePdfUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {resumePdfUploading ? 'Uploading…' : user?.resume_pdf_path ? 'Replace PDF' : 'Upload PDF'}
                  <input type="file" accept=".pdf" className="hidden" onChange={handleResumePdfUpload} />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* ── Danger Zone (Full width bottom section) ── */}
        <div className="mt-8 pt-6 border-t border-border">
          <div className="bg-card border border-red-200 rounded-card p-5 sm:p-6 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-full bg-red-500" />
                <h2 className="text-lg font-semibold text-red-700" style={{ fontFamily: 'Instrument Serif, serif' }}>Danger Zone</h2>
              </div>
              <p className="text-sm text-muted">Permanently delete your account and all data.</p>
            </div>
            <button
              onClick={() => setShowDelete(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-input text-sm font-semibold hover:bg-red-50 hover:border-red-400 transition-colors self-start sm:self-auto flex-shrink-0"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      <Modal open={showDelete} onClose={() => setShowDelete(false)}>
        <div className="p-6">
          <h3 className="text-xl font-semibold text-ink mb-2" style={{ fontFamily: 'Instrument Serif, serif' }}>Delete your account?</h3>
          <p className="text-sm text-muted leading-relaxed mb-6">
            This action is permanent. All your data, applications, and outreach history will be removed.
          </p>
          <div className="flex gap-3">
            <button onClick={() => setShowDelete(false)} className="flex-1 py-2.5 border border-border rounded-input text-sm font-medium text-muted hover:bg-bg transition-colors">
              Cancel
            </button>
            <button
              onClick={handleAccountDelete}
              disabled={accountDeleting}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-input text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {accountDeleting ? 'Deleting…' : 'Delete Account'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Gmail Disconnect Confirmation Modal */}
      <Modal open={showDisconnectModal} onClose={() => setShowDisconnectModal(false)}>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            <h3 className="text-xl font-semibold text-ink" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Disconnect Gmail?
            </h3>
          </div>
          <p className="text-sm text-muted leading-relaxed mb-6">
            EextraMile will no longer be able to detect new job applications or create outreach drafts in your Gmail account. You can reconnect your account at any time.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowDisconnectModal(false)}
              className="flex-1 py-2.5 border border-border rounded-input text-sm font-medium text-muted hover:bg-bg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setShowDisconnectModal(false);
                await handleGmailDisconnect();
              }}
              className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-input text-sm font-semibold transition-colors"
            >
              Disconnect Gmail
            </button>
          </div>
        </div>
      </Modal>

      {/* Logout Confirmation Modal */}
      <Modal open={showLogoutModal} onClose={() => setShowLogoutModal(false)}>
        <div className="p-6">
          <h3 className="text-xl font-semibold text-ink mb-2" style={{ fontFamily: 'Instrument Serif, serif' }}>
            Log out of EextraMile?
          </h3>
          <p className="text-sm text-muted leading-relaxed mb-6">
            You will be signed out of your account on this device. You can sign back in with your Google account anytime.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowLogoutModal(false)}
              className="flex-1 py-2.5 border border-border rounded-input text-sm font-medium text-muted hover:bg-bg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setShowLogoutModal(false);
                await logout();
              }}
              className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-input text-sm font-semibold transition-colors"
            >
              Log Out
            </button>
          </div>
        </div>
      </Modal>

      {confirmDeliveryMode !== null && (
        <DeliveryModeConfirmModal
          open={confirmDeliveryMode !== null}
          targetMode={confirmDeliveryMode}
          onCancel={() => setConfirmDeliveryMode(null)}
          onConfirm={handleConfirmDeliveryMode}
          loading={autoSendUpdating}
        />
      )}

      <ResumePreviewModal open={showResumePreview} onClose={() => setShowResumePreview(false)} />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </DashboardLayout>
  );
}
